import { zodResolver } from '@hookform/resolvers/zod';
import {
  adminServerDetailSchema,
  allocationSchema,
  daemonSyncSchema,
  okSchema,
  paginated,
  updateServerBuildSchema,
  updateServerDetailsSchema,
  updateServerStartupSchema,
  type AdminServerDetail,
} from '@hearth/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  ArrowLeft,
  Ban,
  Loader2,
  Play,
  RotateCcw,
  Skull,
  Square,
  Trash2,
  Wrench,
  SlidersHorizontal as SlidersHorizontalIcon,
} from 'lucide-react';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import type { z } from 'zod';
import { Confirm } from '@/components/confirm';
import { TransferCard } from '@/components/transfer-card';
import { FormField } from '@/components/form-field';
import { PageHeader } from '@/components/page-header';
import { QueryState } from '@/components/query-state';
import { ServerLimitsFields } from '@/components/server-limits-fields';
import { PowerStateBadge, ServerStatusBadge } from '@/components/status-badges';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { apiRequest } from '@/lib/api';
import { userSearchQuery } from '@/lib/admin-queries';
import { applyServerErrors, errorMessage } from '@/lib/forms';
import { dateTime } from '@/lib/format';

const TABS = ['overview', 'details', 'build', 'startup', 'manage'] as const;
type Tab = (typeof TABS)[number];

export const Route = createFileRoute('/_app/admin/servers/$serverId')({
  validateSearch: (s: Record<string, unknown>): { tab?: Tab } => ({
    tab: TABS.includes(s.tab as Tab) ? (s.tab as Tab) : undefined,
  }),
  component: ServerPage,
});

const serverKey = (id: number) => ['admin', 'servers', 'detail', id] as const;
const syncedSchema = adminServerDetailSchema.extend(daemonSyncSchema.shape);

/** After a change that must reach the node: update the cache and warn if the node didn't get it. */
function useApplyResult(id: number) {
  const queryClient = useQueryClient();
  return (result: z.infer<typeof syncedSchema>, success: string) => {
    queryClient.setQueryData(serverKey(id), result);
    void queryClient.invalidateQueries({
      queryKey: ['admin', 'servers'],
      exact: false,
      refetchType: 'none',
    });
    if (result.daemonSynced) toast.success(success);
    else
      toast.warning(
        `Saved, but the node didn't get the change: ${result.daemonError}. It will pick it up when hearthd restarts.`,
      );
  };
}

function ServerPage() {
  const serverId = Number(Route.useParams().serverId);
  const { tab = 'overview' } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const query = useQuery({
    queryKey: serverKey(serverId),
    queryFn: ({ signal }) =>
      apiRequest(`/api/application/servers/${serverId}`, {
        schema: adminServerDetailSchema,
        signal,
      }),
    // Poll while something is in progress.
    refetchInterval: (q) => {
      const d = q.state.data;
      return d &&
        (d.status === 'installing' ||
          d.daemon?.state === 'starting' ||
          d.daemon?.state === 'stopping')
        ? 2000
        : 10_000;
    },
  });
  return (
    <>
      <Button asChild variant="ghost" size="sm" className="mb-4 -ml-2">
        <Link to="/admin/servers">
          <ArrowLeft /> Servers
        </Link>
      </Button>
      <QueryState query={query}>
        {(server) => (
          <>
            <PageHeader
              title={server.name}
              icon={SlidersHorizontalIcon}
              description={
                <span className="flex flex-wrap items-center gap-2">
                  <span className="font-mono text-xs">{server.identifier}</span>
                  {server.daemon && <PowerStateBadge state={server.daemon.state} />}
                  <ServerStatusBadge status={server.status} />
                  {server.daemon?.emulated && (
                    <Badge variant="outline">Emulated {server.daemon.platform}</Badge>
                  )}
                </span>
              }
            />
            <Tabs value={tab} onValueChange={(v) => void navigate({ search: { tab: v as Tab } })}>
              <TabsList>
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="details">Details</TabsTrigger>
                <TabsTrigger value="build">Build</TabsTrigger>
                <TabsTrigger value="startup">Startup</TabsTrigger>
                <TabsTrigger value="manage">Manage</TabsTrigger>
              </TabsList>
              <TabsContent value="overview" className="mt-6">
                <Overview server={server} />
              </TabsContent>
              <TabsContent value="details" className="mt-6">
                <Details server={server} />
              </TabsContent>
              <TabsContent value="build" className="mt-6">
                <Build server={server} />
              </TabsContent>
              <TabsContent value="startup" className="mt-6">
                <Startup server={server} />
              </TabsContent>
              <TabsContent value="manage" className="mt-6">
                <Manage server={server} />
              </TabsContent>
            </Tabs>
          </>
        )}
      </QueryState>
    </>
  );
}

function Overview({ server }: { server: AdminServerDetail }) {
  const queryClient = useQueryClient();
  const power = useMutation({
    mutationFn: (action: 'start' | 'stop' | 'restart' | 'kill') =>
      apiRequest(`/api/application/servers/${server.id}/power`, {
        method: 'POST',
        body: { action },
        schema: okSchema,
      }),
    onSuccess: (_, action) => {
      toast.success(`Sent ${action}.`);
      setTimeout(() => void queryClient.invalidateQueries({ queryKey: serverKey(server.id) }), 500);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const state = server.daemon?.state;
  const blocked = server.status !== null;
  const address = `${server.allocation.ipAlias ?? server.allocation.ip}:${server.allocation.port}`;
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <Card>
        <CardHeader>
          <CardTitle>Power</CardTitle>
          <CardDescription>
            The live console arrives with the next milestone; for now control the process here.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4">
          {server.daemonError && (
            <Alert variant="destructive">
              <AlertTitle>Node unreachable</AlertTitle>
              <AlertDescription>{server.daemonError}</AlertDescription>
            </Alert>
          )}
          {server.status === 'installing' && (
            <Alert>
              <Loader2 className="animate-spin" />
              <AlertTitle>Installing</AlertTitle>
              <AlertDescription>
                The egg’s install script is running on {server.node.name}.
              </AlertDescription>
            </Alert>
          )}
          {(server.status === 'install_failed' || server.status === 'reinstall_failed') && (
            <Alert variant="destructive">
              <AlertTitle>Install failed</AlertTitle>
              <AlertDescription>
                Check the egg’s install script, then reinstall from the Manage tab.
              </AlertDescription>
            </Alert>
          )}
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={() => power.mutate('start')}
              disabled={blocked || state !== 'offline' || power.isPending}
            >
              <Play /> Start
            </Button>
            <Button
              variant="outline"
              onClick={() => power.mutate('restart')}
              disabled={blocked || state === 'offline' || power.isPending}
            >
              <RotateCcw /> Restart
            </Button>
            <Button
              variant="outline"
              onClick={() => power.mutate('stop')}
              disabled={state === 'offline' || state === 'stopping' || power.isPending}
            >
              <Square /> Stop
            </Button>
            <Button
              variant="outline"
              className="text-destructive"
              onClick={() => power.mutate('kill')}
              disabled={state === 'offline' || power.isPending}
            >
              <Skull /> Kill
            </Button>
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-2 text-sm">
            <dt className="text-muted-foreground">Address</dt>
            <dd className="font-mono">{address}</dd>
            <dt className="text-muted-foreground">Owner</dt>
            <dd>
              <Link
                to="/admin/users/$userId"
                params={{ userId: String(server.owner.id) }}
                className="hover:underline"
              >
                {server.owner.username}
              </Link>
            </dd>
            <dt className="text-muted-foreground">Node</dt>
            <dd>
              <Link
                to="/admin/nodes/$nodeId"
                params={{ nodeId: String(server.node.id) }}
                className="hover:underline"
              >
                {server.node.name}
              </Link>
            </dd>
            <dt className="text-muted-foreground">Egg</dt>
            <dd>
              <Link
                to="/admin/eggs/$eggId"
                params={{ eggId: String(server.egg.id) }}
                className="hover:underline"
              >
                {server.nest.name} / {server.egg.name}
              </Link>
            </dd>
            <dt className="text-muted-foreground">Image</dt>
            <dd className="font-mono text-xs break-all">{server.image}</dd>
            <dt className="text-muted-foreground">Limits</dt>
            <dd>
              {server.limits.memoryMb || '∞'} MiB RAM · {server.limits.diskMb} MiB disk ·{' '}
              {server.limits.cpuPercent || '∞'}% CPU
            </dd>
            <dt className="text-muted-foreground">Installed</dt>
            <dd>{server.installedAt ? dateTime(server.installedAt) : 'not yet'}</dd>
          </dl>
        </CardContent>
      </Card>
    </div>
  );
}

function Details({ server }: { server: AdminServerDetail }) {
  const queryClient = useQueryClient();
  const [userSearch, setUserSearch] = useState('');
  const users = useQuery(userSearchQuery(userSearch));
  type Values = z.input<typeof updateServerDetailsSchema>;
  const form = useForm<Values>({
    resolver: zodResolver(updateServerDetailsSchema),
    defaultValues: { name: server.name, description: server.description, ownerId: server.owner.id },
  });
  const save = useMutation({
    mutationFn: (body: Values) =>
      apiRequest(`/api/application/servers/${server.id}/details`, {
        method: 'PATCH',
        body,
        schema: adminServerDetailSchema,
      }),
    onSuccess: (d) => {
      queryClient.setQueryData(serverKey(server.id), d);
      toast.success('Details saved.');
    },
    onError: (e) => applyServerErrors(e, form.setError),
  });
  const options = [server.owner, ...(users.data ?? []).filter((u) => u.id !== server.owner.id)];
  return (
    <Card>
      <form onSubmit={form.handleSubmit((v) => save.mutate(v))} noValidate>
        <CardContent className="grid gap-4 pt-6 sm:grid-cols-2">
          <FormField label="Name" error={form.formState.errors.name?.message}>
            <Input {...form.register('name')} />
          </FormField>
          <Controller
            control={form.control}
            name="ownerId"
            render={({ field }) => (
              <FormField label="Owner" error={form.formState.errors.ownerId?.message}>
                <div className="grid gap-2">
                  <Input
                    placeholder="Search users…"
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    aria-label="Search owners"
                  />
                  <Select
                    value={String(field.value)}
                    onValueChange={(v) => field.onChange(Number(v))}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {options.map((u) => (
                        <SelectItem key={u.id} value={String(u.id)}>
                          {u.username} ({u.email})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </FormField>
            )}
          />
          <div className="sm:col-span-2">
            <FormField label="Description" error={form.formState.errors.description?.message}>
              <Textarea rows={3} {...form.register('description')} />
            </FormField>
          </div>
        </CardContent>
        <CardFooter className="mt-6 justify-end">
          <Button type="submit" disabled={save.isPending || !form.formState.isDirty}>
            {save.isPending && <Loader2 className="animate-spin" />} Save
          </Button>
        </CardFooter>
      </form>
    </Card>
  );
}

function Build({ server }: { server: AdminServerDetail }) {
  const apply = useApplyResult(server.id);
  type Values = z.input<typeof updateServerBuildSchema>;
  const form = useForm<Values>({
    resolver: zodResolver(updateServerBuildSchema),
    defaultValues: {
      limits: { ...server.limits, threads: server.limits.threads ?? '' },
      featureLimits: server.featureLimits,
      allocationId: server.allocation.id,
      addAllocationIds: [],
      removeAllocationIds: [],
    },
  });
  const free = useQuery({
    queryKey: ['admin', 'nodes', server.node.id, 'allocations', 'free-all'],
    queryFn: ({ signal }) =>
      apiRequest(`/api/application/nodes/${server.node.id}/allocations?filter=free&perPage=100`, {
        schema: paginated(allocationSchema),
        signal,
      }),
    select: (d) => d.data,
  });
  const save = useMutation({
    mutationFn: (body: Values) =>
      apiRequest(`/api/application/servers/${server.id}/build`, {
        method: 'PATCH',
        body,
        schema: syncedSchema,
      }),
    onSuccess: (d) => {
      apply(d, 'Build saved. Restart the server to apply new limits.');
      form.reset({
        ...form.getValues(),
        addAllocationIds: [],
        removeAllocationIds: [],
        allocationId: d.allocation.id,
      });
    },
    onError: (e) => applyServerErrors(e, form.setError),
  });
  const owned = [server.allocation, ...server.additionalAllocations];
  const add = form.watch('addAllocationIds') ?? [];
  const remove = form.watch('removeAllocationIds') ?? [];
  const candidates = [
    ...owned.filter((a) => !remove.includes(a.id)),
    ...(free.data ?? []).filter((a) => add.includes(a.id)),
  ];
  const toggle = (name: 'addAllocationIds' | 'removeAllocationIds', id: number, on: boolean) => {
    const current = form.getValues(name) ?? [];
    form.setValue(name, on ? [...current, id] : current.filter((x) => x !== id), {
      shouldDirty: true,
    });
  };
  return (
    <form onSubmit={form.handleSubmit((v) => save.mutate(v))} noValidate className="grid gap-6">
      <Card>
        <CardHeader>
          <CardTitle>Resources</CardTitle>
        </CardHeader>
        <CardContent>
          <ServerLimitsFields register={form.register} errors={form.formState.errors} />
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Allocations</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <Controller
            control={form.control}
            name="allocationId"
            render={({ field }) => (
              <FormField
                label="Primary allocation"
                error={form.formState.errors.allocationId?.message}
              >
                <Select
                  value={String(field.value)}
                  onValueChange={(v) => field.onChange(Number(v))}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {candidates.map((a) => (
                      <SelectItem key={a.id} value={String(a.id)}>
                        {a.ipAlias ?? a.ip}:{a.port}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormField>
            )}
          />
          <div className="grid gap-2">
            <span className="text-sm font-medium">Assigned (untick to remove)</span>
            <div className="flex flex-wrap gap-x-4 gap-y-2 rounded-md border p-3">
              {owned.map((a) => (
                <label key={a.id} className="flex items-center gap-2 font-mono text-xs">
                  <Checkbox
                    checked={!remove.includes(a.id)}
                    onCheckedChange={(c) => toggle('removeAllocationIds', a.id, c !== true)}
                  />
                  {a.port}
                </label>
              ))}
            </div>
          </div>
          {(free.data?.length ?? 0) > 0 && (
            <div className="grid gap-2 sm:col-span-2">
              <span className="text-sm font-medium">Add free allocations</span>
              <div className="flex max-h-32 flex-wrap gap-x-4 gap-y-2 overflow-y-auto rounded-md border p-3">
                {free.data?.map((a) => (
                  <label key={a.id} className="flex items-center gap-2 font-mono text-xs">
                    <Checkbox
                      checked={add.includes(a.id)}
                      onCheckedChange={(c) => toggle('addAllocationIds', a.id, c === true)}
                    />
                    {a.port}
                  </label>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      <div className="flex justify-end">
        <Button type="submit" disabled={save.isPending || !form.formState.isDirty}>
          {save.isPending && <Loader2 className="animate-spin" />} Save build
        </Button>
      </div>
    </form>
  );
}

function Startup({ server }: { server: AdminServerDetail }) {
  const apply = useApplyResult(server.id);
  type Values = z.input<typeof updateServerStartupSchema>;
  const form = useForm<Values>({
    resolver: zodResolver(updateServerStartupSchema),
    defaultValues: {
      startup: server.startup,
      image: server.image,
      skipScripts: server.skipScripts,
      variables: Object.fromEntries(server.variables.map((v) => [v.envVariable, v.value])),
    },
  });
  const save = useMutation({
    mutationFn: (body: Values) =>
      apiRequest(`/api/application/servers/${server.id}/startup`, {
        method: 'PATCH',
        body,
        schema: syncedSchema,
      }),
    onSuccess: (d) => apply(d, 'Startup saved. Restart the server to apply it.'),
    onError: (e) => applyServerErrors(e, form.setError),
  });
  const errors = form.formState.errors;
  const imageOptions = Object.entries(server.eggImages);
  const customImage = !Object.values(server.eggImages).includes(form.watch('image'));
  return (
    <form onSubmit={form.handleSubmit((v) => save.mutate(v))} noValidate className="grid gap-6">
      <Card>
        <CardHeader>
          <CardTitle>Command & image</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4">
          <FormField
            label="Startup command"
            error={errors.startup?.message}
            description="Use {{VARIABLE}} placeholders."
          >
            <Textarea rows={2} className="font-mono text-xs" {...form.register('startup')} />
          </FormField>
          <Controller
            control={form.control}
            name="image"
            render={({ field }) => (
              <FormField
                label="Docker image"
                error={errors.image?.message}
                description={customImage ? 'Custom image (not one of the egg’s).' : undefined}
              >
                <div className="grid gap-2 sm:grid-cols-2">
                  <Select value={customImage ? '' : field.value} onValueChange={field.onChange}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Custom" />
                    </SelectTrigger>
                    <SelectContent>
                      {imageOptions.map(([label, img]) => (
                        <SelectItem key={img} value={img}>
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    className="font-mono text-xs"
                    value={field.value}
                    onChange={field.onChange}
                    aria-label="Image reference"
                  />
                </div>
              </FormField>
            )}
          />
          <Controller
            control={form.control}
            name="skipScripts"
            render={({ field }) => (
              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={field.value}
                  onCheckedChange={(c) => field.onChange(c === true)}
                />{' '}
                Skip the egg’s install script on reinstall
              </label>
            )}
          />
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Variables</CardTitle>
          <CardDescription>
            Admins can edit every variable, including ones hidden from users.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          {server.variables.length === 0 && (
            <p className="text-muted-foreground text-sm">This egg has no variables.</p>
          )}
          {server.variables.map((v) => (
            <FormField
              key={v.envVariable}
              label={`${v.name}${v.userEditable ? '' : ' (admin only)'}`}
              error={
                (errors.variables as Record<string, { message?: string }> | undefined)?.[
                  v.envVariable
                ]?.message
              }
              description={
                <>
                  <code>{v.envVariable}</code> · {v.rules}
                </>
              }
            >
              <Input
                className="font-mono text-xs"
                {...form.register(`variables.${v.envVariable}`)}
              />
            </FormField>
          ))}
        </CardContent>
      </Card>
      <div className="flex justify-end">
        <Button type="submit" disabled={save.isPending || !form.formState.isDirty}>
          {save.isPending && <Loader2 className="animate-spin" />} Save startup
        </Button>
      </div>
    </form>
  );
}

function Manage({ server }: { server: AdminServerDetail }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const apply = useApplyResult(server.id);
  const suspend = useMutation({
    mutationFn: () =>
      apiRequest(
        `/api/application/servers/${server.id}/${server.status === 'suspended' ? 'unsuspend' : 'suspend'}`,
        { method: 'POST', schema: syncedSchema },
      ),
    onSuccess: (d) =>
      apply(d, d.status === 'suspended' ? 'Server suspended and stopped.' : 'Server unsuspended.'),
    onError: (e) => toast.error(errorMessage(e)),
  });
  const reinstall = useMutation({
    mutationFn: () =>
      apiRequest(`/api/application/servers/${server.id}/reinstall`, {
        method: 'POST',
        schema: okSchema,
      }),
    onSuccess: async () => {
      toast.success('Reinstall started.');
      await queryClient.invalidateQueries({ queryKey: serverKey(server.id) });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const remove = useMutation({
    mutationFn: (force: boolean) =>
      apiRequest(`/api/application/servers/${server.id}${force ? '?force=true' : ''}`, {
        method: 'DELETE',
        schema: okSchema,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin'] });
      toast.success(`Deleted ${server.name}.`);
      await navigate({ to: '/admin/servers' });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const suspended = server.status === 'suspended';
  return (
    <div className="grid gap-4">
      <TransferCard server={server} />
      <ActionCard
        title="Reinstall"
        description="Stops the server and runs the egg’s install script again. Existing files may be overwritten."
        action={
          <Confirm
            title="Reinstall this server?"
            description="The install script may overwrite files."
            action="Reinstall"
            onConfirm={() => reinstall.mutate()}
          >
            <Button
              variant="outline"
              disabled={suspended || server.status === 'installing' || reinstall.isPending}
            >
              <Wrench /> Reinstall
            </Button>
          </Confirm>
        }
      />
      <ActionCard
        title={suspended ? 'Unsuspend' : 'Suspend'}
        description={
          suspended
            ? 'Let the owner start and use the server again.'
            : 'Stop the server and block its owner and subusers from using it.'
        }
        action={
          <Confirm
            title={suspended ? 'Unsuspend this server?' : 'Suspend this server?'}
            description={suspended ? 'It can be started again.' : 'It will be killed immediately.'}
            action={suspended ? 'Unsuspend' : 'Suspend'}
            destructive={!suspended}
            onConfirm={() => suspend.mutate()}
          >
            <Button variant="outline" disabled={suspend.isPending}>
              <Ban /> {suspended ? 'Unsuspend' : 'Suspend'}
            </Button>
          </Confirm>
        }
      />
      <ActionCard
        title="Delete"
        description="Removes the container and all of the server’s files from the node. Use force delete only if the node is gone for good."
        destructive
        action={
          <div className="flex flex-wrap gap-2">
            <Confirm
              title={`Delete ${server.name}?`}
              description="Its files are deleted from the node. This can't be undone."
              action="Delete"
              destructive
              onConfirm={() => remove.mutate(false)}
            >
              <Button variant="destructive" disabled={remove.isPending}>
                <Trash2 /> Delete
              </Button>
            </Confirm>
            <Confirm
              title="Force delete?"
              description="Removes the server from the panel even if the node can't be reached. Its files and container may be left behind on the node."
              action="Force delete"
              destructive
              onConfirm={() => remove.mutate(true)}
            >
              <Button variant="outline" className="text-destructive" disabled={remove.isPending}>
                Force delete
              </Button>
            </Confirm>
          </div>
        }
      />
    </div>
  );
}

function ActionCard({
  title,
  description,
  action,
  destructive,
}: {
  title: string;
  description: string;
  action: React.ReactNode;
  destructive?: boolean;
}) {
  return (
    <Card className={destructive ? 'border-destructive/40' : undefined}>
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-4">
        <div className="grid gap-1.5">
          <CardTitle>{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </div>
        {action}
      </CardHeader>
    </Card>
  );
}
